import { drawRoundRect } from "@gameApp/helpers/canvas/draw/rectRounded";
import { drawTextPure } from "@gameApp/helpers/canvas/draw/textPure";
import { getContextFont } from "@gameApp/helpers/canvas/getContextFont";
import { measureText } from "@gameApp/helpers/canvas/measureText";
import { isStringEmpty } from "@gameApp/utils/strings/isStringEmpty";
import {
  BASE_FONT_SIZE,
  BLOCK_BORDER_RADIUS,
  BLOCK_HEIGHT,
  BLOCK_HORIZONTAL_PADDING,
  BLOCK_VERTICAL_PADDING,
} from "@gameComponents/MiniGame/OrderOverview/Snapshot/constants";
import colors from "tailwindcss/colors";

interface Params {
  context: CanvasRenderingContext2D;
  usersRate?: string;
  translateX: number;
  translateY: number;
  width: number;
  isWin: boolean;
}

const DEFAULT_TEXT_OPTIONS = { size: BASE_FONT_SIZE, weight: 500 };
const PERCENT_TEXT_OPTIONS = { size: BASE_FONT_SIZE, weight: 700 };

export function miniGameOrderOverviewSnapshotDrawUserInsights(
  params: Params,
): void {
  const { context, usersRate, translateX, translateY, width, isWin } = params;

  const messages = getMessages(isWin, usersRate);
  const initialY = translateY + BLOCK_VERTICAL_PADDING + BASE_FONT_SIZE;

  if (messages) {
    drawBackground(context, translateX, translateY, width);
    drawTexts(
      context,
      messages,
      translateX + BLOCK_HORIZONTAL_PADDING,
      initialY,
    );
  }
}

function getMessages(isWin: boolean, usersRate?: string) {
  if (isStringEmpty(usersRate)) {
    return;
  }

  return {
    greet: isWin ? "🧠 Smart! " : "🐹 No Way! ",
    percentText: `${usersRate}% `,
    restText: "of DROPS Miniapp users",
    restTextSecondLine: isWin
      ? "also placed correct order and won!"
      : "also placed wrong order and lost.",
  };
}

function drawBackground(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
): void {
  context.fillStyle = colors.gray[900];
  drawRoundRect({
    context,
    x,
    y,
    width,
    height: BLOCK_HEIGHT,
    radius: BLOCK_BORDER_RADIUS,
    stroke: false,
  });
}

function drawTexts(
  context: CanvasRenderingContext2D,
  messages: {
    greet: string;
    percentText: string;
    restText: string;
    restTextSecondLine: string;
  },
  translateX: number,
  initialY: number,
): void {
  const { greet, percentText, restText, restTextSecondLine } = messages;

  const greetWidth = drawText(
    context,
    greet,
    translateX,
    initialY,
    colors.gray[400],
    DEFAULT_TEXT_OPTIONS,
  );

  const percentTextX = translateX + greetWidth;
  const percentTextWidth = drawText(
    context,
    percentText,
    percentTextX,
    initialY,
    colors.white,
    PERCENT_TEXT_OPTIONS,
  );

  const restTextX = percentTextX + percentTextWidth;
  drawText(
    context,
    restText,
    restTextX,
    initialY,
    colors.gray[400],
    DEFAULT_TEXT_OPTIONS,
  );

  drawText(
    context,
    restTextSecondLine,
    translateX,
    initialY + 20,
    colors.gray[400],
    DEFAULT_TEXT_OPTIONS,
  );
}

function drawText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  fontOptions: { size: number; weight: number },
): number {
  drawTextPure({
    context,
    text,
    x,
    y,
    contextOptions: {
      font: getContextFont(fontOptions),
      fillStyle: color,
    },
  });

  const { width } = measureText(text, (ctx) => {
    ctx.font = getContextFont(fontOptions);
  });

  return width;
}
